import React, { useEffect, useRef } from 'react';
import { LEVELS } from './PixelField';
type Props = {
  className?: string;
  /** Same pixel size as the footer field */
  cell?: number;
};

// Seconds between pulses, and how long one ring takes to travel out
const PERIOD = 4.2;
const TRAVEL = 6;
// How far a ring reaches, as a share of the section's diagonal
const REACH = 0.55;
// Overall strength — kept very low so it sits behind the ledger
const GAIN = 0.55;
// Ring thickness in cells
const BAND = 2.2;

/**
 * A very quiet pixel pulse anchored in the bottom-left corner of the awards section.
 * A faint dithered glow rests in the corner, and every few seconds a soft ring of pixels
 * breathes outward from it and fades long before it reaches the content.
 * Same square pixels and brightness steps as the footer field. Runs only while on screen.
 */
export function AwardsPulse({
  className = '',
  cell = 14
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !host || !ctx) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cols = 0;
    let rows = 0;
    let raf = 0;
    let visible = false;
    let noise = new Float32Array(0);
    const start = performance.now();
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const {
        width,
        height
      } = host.getBoundingClientRect();
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / cell);
      rows = Math.ceil(height / cell);
      // Fixed per-cell dither so the corner glow reads as pixels, not a smooth blur
      noise = Float32Array.from({
        length: cols * rows
      }, () => Math.random());
    };
    const draw = (now: number) => {
      const t = reduce ? 0 : (now - start) / 1000;
      const maxR = Math.hypot(cols, rows) * REACH;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';

      // Ring radii currently in flight (overlapping pulses allowed)
      const rings: {
        r: number;
        life: number;
      }[] = [];
      if (!reduce) {
        for (let k = 0; k < Math.ceil(TRAVEL / PERIOD) + 1; k++) {
          const age = t % PERIOD + k * PERIOD;
          if (age > TRAVEL) continue;
          const p = age / TRAVEL;
          // Ease-out travel so each ring slows and settles as it fades
          const eased = 1 - Math.pow(1 - p, 3);
          rings.push({
            r: eased * maxR,
            life: (1 - p) * (1 - p)
          });
        }
      }

      // Slow breathing of the resting glow
      const breath = reduce ? 0.5 : 0.5 + 0.5 * Math.sin(t / PERIOD * Math.PI * 2 - Math.PI / 2);
      for (let y = 0; y < rows; y++) {
        // Distance measured from the bottom-left corner cell
        const dy = rows - 1 - y;
        for (let x = 0; x < cols; x++) {
          const d = Math.hypot(x, dy);
          if (d > maxR + BAND) continue;

          // Resting corner glow, densest right at the corner
          const falloff = Math.max(0, 1 - d / (maxR * 0.45));
          let v = falloff * falloff * (0.35 + breath * 0.25);
          for (const ring of rings) {
            const off = Math.abs(d - ring.r);
            if (off < BAND) v += (1 - off / BAND) * ring.life * 0.9;
          }
          v *= GAIN;
          // Dither threshold keeps the edges broken up and pixelated
          if (v < noise[y * cols + x] * 0.5) continue;
          const level = LEVELS[Math.min(LEVELS.length - 1, Math.ceil(v * (LEVELS.length - 1)))];
          if (level <= 0) continue;
          ctx.globalAlpha = level;
          ctx.fillRect(x * cell, y * cell, cell - 1, cell - 1);
        }
      }
      ctx.globalAlpha = 1;
      if (visible && !reduce) raf = requestAnimationFrame(draw);
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      cancelAnimationFrame(raf);
      if (visible) raf = requestAnimationFrame(draw);
    });
    const ro = new ResizeObserver(() => {
      resize();
      draw(performance.now());
    });
    resize();
    ro.observe(host);
    io.observe(host);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
    };
  }, [cell]);
  return <canvas ref={canvasRef} aria-hidden="true" className={`pixel-anim pointer-events-none absolute inset-0 ${className}`} data-id="element-43" data-mp="sm30s4fz5c5" />;
}